import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Checkbox,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { makeNewId } from "common/ids";
import AutoCompleteTextField from "components/common/AutoCompleteTextField";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { connect } from "react-redux";
import * as towerPricingActions from "store/actions/pricing/tower/towerPricingActions";
import * as metaSelectors from "store/selectors/meta/metaSelectors";
import * as towerPricingSelectors from "store/selectors/pricing/tower/towerPricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const DND_TYPE_SCHEDULE_OF_UNDERLYING = "SCHEDULE_OF_UNDERLYING";

// A drop target for one slot of the schedule. Dropping inserts the dragged
// row BEFORE the row at `index` (the add line is the slot after the last
// row), the same semantics as the other react-dnd lists (Subjectivities,
// Endorsements, Terms and Conditions). `isOver` is false while hovering the
// dragged row itself, so it never draws an insertion line on itself.
const useScheduleDropTarget = ({ index, moveRow, canReorder }) =>
  useDrop(
    () => ({
      accept: DND_TYPE_SCHEDULE_OF_UNDERLYING,
      canDrop: () => canReorder,
      drop: (item) => moveRow(item.index, index),
      collect: (monitor) => ({
        isOver:
          monitor.isOver() &&
          monitor.canDrop() &&
          monitor.getItem()?.index !== index,
      }),
    }),
    [moveRow, index, canReorder]
  );

// Insertion line drawn above a drop target. An inset shadow does not change
// the row height, so the table does not reflow as the drag passes each row.
const insertionLineSx = (isOver) => (theme) => ({
  "& > td": isOver
    ? { boxShadow: `inset 0 2px 0 0 ${theme.palette.primary.main}` }
    : {},
});

const PRODUCT_TYPES = [
  { key: "GL", label: "General Liability" },
  { key: "AL", label: "Auto Liability" },
  { key: "EL", label: "Employer Liability" },
  { key: "XS", label: "Excess Liability" },
  { key: "Other", label: "Other" },
];

const PRODUCT_FIELDS = {
  GL: [
    { key: "eachOccurrence", label: "Each Occurrence" },
    { key: "generalAggregate", label: "General Aggregate" },
    {
      key: "productsCompletedOpsAggregate",
      label: "Products-Completed Operations Aggregate",
    },
    {
      key: "personalAdvertisingInjury",
      label: "Personal & Advertising Injury",
    },
    {
      key: "employeeBenefitsEachEmployee",
      label: "Employee Benefits Each Employee",
    },
    { key: "employeeBenefitsAggregate", label: "Employee Benefits Aggregate" },
    {
      key: "liquorLiabilityEachCommonCause",
      label: "Liquor Liability Each Common Cause Limit",
    },
    {
      key: "liquorLiabilityAggregate",
      label: "Liquor Liability Aggregate Limit",
    },
  ],
  AL: [{ key: "eachAccident", label: "Each Accident" }],
  EL: [
    { key: "eachAccident", label: "Each Accident" },
    { key: "aggregate", label: "Aggregate" },
    { key: "eachEmployee", label: "Each Employee" },
  ],
  XS: [
    { key: "eachOccurrence", label: "Each Occurrence" },
    { key: "generalAggregate", label: "General Aggregate" },
    {
      key: "controlling",
      label: "Controlling",
      type: "select",
      options: ["Yes", "No"],
    },
  ],
};

const LimitField = ({ label, value, onChange }) => (
  <Grid item xs={6} sm={4} md={3}>
    <PrettyNumberTextField
      label={label}
      value={value ?? ""}
      onChangeNumber={onChange}
      variant={"filled"}
      size={"small"}
      fullWidth
    />
  </Grid>
);

const SelectField = ({ label, options, value, onChange }) => (
  <Grid item xs={6} sm={4} md={3}>
    <FormControl fullWidth size={"small"} variant={"filled"}>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        displayEmpty
        label={label}
      >
        <MenuItem value={""}>{"Select..."}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
);

const makeCustomField = () => ({
  id: makeNewId(),
  label: "",
  value: "",
});

const CustomFields = ({ fields, onChange }) => {
  const rows =
    fields.length > 0 ? fields : [{ ...makeCustomField(), id: "_empty" }];

  const persist = (updated) => {
    onChange(
      updated.map((f) => (f.id === "_empty" ? { ...f, id: makeNewId() } : f))
    );
  };

  const addCustomField = () => {
    persist([...rows, makeCustomField()]);
  };

  const updateCustomField = (id, key, val) => {
    persist(rows.map((f) => (f.id === id ? { ...f, [key]: val } : f)));
  };

  const deleteCustomField = (id) => {
    onChange(rows.filter((f) => f.id !== id && f.id !== "_empty"));
  };

  return (
    <Box>
      {rows.map((field) => (
        <Grid container spacing={1.5} key={field.id} sx={{ mb: 1 }}>
          <Grid item xs={4}>
            <TextField
              label={"Field Name"}
              value={field.label ?? ""}
              onChange={(e) =>
                updateCustomField(field.id, "label", e.target.value)
              }
              variant={"filled"}
              size={"small"}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={"Value"}
              value={field.value ?? ""}
              onChange={(e) =>
                updateCustomField(field.id, "value", e.target.value)
              }
              variant={"filled"}
              size={"small"}
              fullWidth
            />
          </Grid>
          <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              icon={DeleteIcon}
              onClick={() => deleteCustomField(field.id)}
            />
          </Grid>
        </Grid>
      ))}
      <Button size={"small"} startIcon={<AddIcon />} onClick={addCustomField}>
        {"Add Field"}
      </Button>
    </Box>
  );
};

const ScheduleOfUnderlyingRow = ({
  layer,
  index,
  updateField,
  addRow,
  deleteRow,
  moveRow,
  canReorder,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only the handle starts a drag (the row is full of inputs); the row and
  // its expanded detail row are both drop targets, and the row is the preview.
  const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(
    () => ({
      type: DND_TYPE_SCHEDULE_OF_UNDERLYING,
      item: { index },
      canDrag: () => canReorder,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index, canReorder]
  );
  const [{ isOver }, dropRef] = useScheduleDropTarget({
    index,
    moveRow,
    canReorder,
  });
  const [{ isOver: isOverDetail }, detailDropRef] = useScheduleDropTarget({
    index,
    moveRow,
    canReorder,
  });
  const layerId = layer?.id;
  const productType = layer?.productType ?? null;
  const isOther = productType === "Other";
  const productFields = productType ? PRODUCT_FIELDS[productType] ?? [] : [];
  const hasExpandableContent = isOther || productFields.length > 0;

  return (
    <>
      <TableRow
        ref={(el) => {
          dropRef(el);
          dragPreviewRef(el);
        }}
        sx={[
          { opacity: isDragging ? 0.4 : 1 },
          insertionLineSx(isOver || isOverDetail),
        ]}
      >
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <IconButton icon={AddIcon} onClick={() => addRow()} />
          <IconButton icon={DeleteIcon} onClick={() => deleteRow(layerId)} />
        </TableCell>
        <TableCell sx={{ minWidth: 160 }}>
          <FormControl fullWidth size={"small"} variant={"filled"}>
            <InputLabel shrink>{"Product"}</InputLabel>
            <Select
              value={productType ?? ""}
              onChange={(e) =>
                updateField(layerId, "productType", e.target.value || null)
              }
              displayEmpty
              label={"Product"}
            >
              <MenuItem value={""}>{"Select..."}</MenuItem>
              {PRODUCT_TYPES.map(({ key, label }) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell sx={{ minWidth: 140 }}>
          <AutoCompleteTextField
            category={"ulCarriers"}
            value={layer?.carrier ?? ""}
            onChange={(e) => updateField(layerId, "carrier", e.target.value)}
            onChangeValue={(v) => updateField(layerId, "carrier", v)}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <TextField
            value={layer?.policyNumber ?? ""}
            onChange={(e) =>
              updateField(layerId, "policyNumber", e.target.value)
            }
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <TextField
            value={layer?.policyPeriod ?? ""}
            onChange={(e) =>
              updateField(layerId, "policyPeriod", e.target.value)
            }
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <PrettyNumberTextField
            value={layer?.premium ?? ""}
            onChangeNumber={(v) => updateField(layerId, "premium", v)}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
            inputProps={{ style: { textAlign: "right" } }}
          />
        </TableCell>
        <TableCell align={"center"}>
          <Checkbox
            checked={layer?.print ?? true}
            onChange={(e) => updateField(layerId, "print", e.target.checked)}
          />
        </TableCell>
        <TableCell align={"right"} sx={{ whiteSpace: "nowrap" }}>
          {productType && (
            <IconButton
              icon={isExpanded ? ExpandLessIcon : ExpandMoreIcon}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
          {canReorder && (
            <Box
              ref={dragRef}
              component={"span"}
              aria-label={"Drag to reorder"}
              title={"Drag to reorder"}
              sx={{
                display: "inline-flex",
                verticalAlign: "middle",
                cursor: "grab",
                color: "text.secondary",
              }}
            >
              <DragIndicatorIcon fontSize={"small"} />
            </Box>
          )}
        </TableCell>
      </TableRow>
      {productType && (
        <TableRow ref={detailDropRef}>
          <TableCell
            sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 0 }}
            colSpan={8}
          >
            <Collapse in={isExpanded} timeout={"auto"} unmountOnExit>
              <Box sx={{ py: 1.5, px: 2 }}>
                {hasExpandableContent && (
                  <>
                    <Typography
                      variant={"caption"}
                      color={"text.secondary"}
                      sx={{ mb: 1, display: "block" }}
                    >
                      {isOther
                        ? "Custom Fields"
                        : `${
                            PRODUCT_TYPES.find((p) => p.key === productType)
                              ?.label ?? productType
                          } Limits`}
                    </Typography>
                    {isOther ? (
                      <CustomFields
                        fields={layer?.customFields ?? []}
                        onChange={(fields) =>
                          updateField(layerId, "customFields", fields)
                        }
                      />
                    ) : (
                      <Grid container spacing={1.5}>
                        {productFields.map(({ key, label, type, options }) =>
                          type === "select" ? (
                            <SelectField
                              key={key}
                              label={label}
                              options={options}
                              value={layer?.[key]}
                              onChange={(v) => updateField(layerId, key, v)}
                            />
                          ) : (
                            <LimitField
                              key={key}
                              label={label}
                              value={layer?.[key]}
                              onChange={(v) => updateField(layerId, key, v)}
                            />
                          )
                        )}
                      </Grid>
                    )}
                  </>
                )}
                <TextField
                  label={"Comment"}
                  value={layer?.comment ?? ""}
                  onChange={(e) =>
                    updateField(layerId, "comment", e.target.value)
                  }
                  variant={"filled"}
                  size={"small"}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mt: hasExpandableContent ? 1.5 : 0 }}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// The add line is also the drop slot after the last row, so a row can be
// dragged to the end of the schedule.
const ScheduleOfUnderlyingAddLine = ({
  index,
  addRow,
  moveRow,
  canReorder,
}) => {
  const [{ isOver }, dropRef] = useScheduleDropTarget({
    index,
    moveRow,
    canReorder,
  });
  return (
    <TableRow ref={dropRef} sx={insertionLineSx(isOver)}>
      <TableCell>
        <IconButton icon={AddIcon} onClick={() => addRow()} />
      </TableCell>
      <TableCell colSpan={7} />
    </TableRow>
  );
};

const ScheduleOfUnderlyingTable = ({
  layers,
  updateField,
  addRow,
  deleteRow,
  moveRow,
  canReorder,
}) => {
  return (
    <Disabled ifReadOnly>
      <Typography>{"Schedule of Underlying"}</Typography>
      <TableContainer>
        <Table size={"small"} className={"condensed-table"}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }} />
              <TableCell>{"Product"}</TableCell>
              <TableCell>{"Carrier"}</TableCell>
              <TableCell>{"Policy #"}</TableCell>
              <TableCell>{"Policy Period"}</TableCell>
              <TableCell>{"Premium"}</TableCell>
              <TableCell align={"center"} sx={{ width: 60 }}>
                {"Print"}
              </TableCell>
              <TableCell sx={{ width: 72 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {(layers ?? []).map((layer, index) => (
              <ScheduleOfUnderlyingRow
                key={layer.id}
                layer={layer}
                index={index}
                updateField={updateField}
                addRow={addRow}
                deleteRow={deleteRow}
                moveRow={moveRow}
                canReorder={canReorder}
              />
            ))}
            <ScheduleOfUnderlyingAddLine
              index={(layers ?? []).length}
              addRow={addRow}
              moveRow={moveRow}
              canReorder={canReorder}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Disabled>
  );
};

const mapStateToProps = (state) => ({
  layers: towerPricingSelectors.getScheduleOfUnderlyingLayers(state),
  // Same rule as <Disabled ifReadOnly>, which cannot reach inside the table.
  canReorder:
    !userSelectors.isUserReadOnly(state) &&
    metaSelectors.selectOverlay(state) == null,
});

const mapDispatchToProps = {
  updateField: towerPricingActions.updateScheduleOfUnderlyingField,
  addRow: towerPricingActions.addScheduleOfUnderlyingRow,
  deleteRow: towerPricingActions.deleteScheduleOfUnderlyingRow,
  moveRow: towerPricingActions.moveScheduleOfUnderlyingRow,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ScheduleOfUnderlyingTable);
